import * as React from 'react';
import { ApolloError, gql, useApolloClient } from '@apollo/client';

import {
  CreateEditImporterModalController,
  CreateEditImporterModalControllerProps
} from './CreateEditImporterModal.controller';
import {
  CreateAutoImportMutationOptions,
  EditAutoImportMutationOptions,
  GetAutoImportsDocument,
  GetAutoImportsQuery,
  GetAutoImportsQueryVariables,
  useCreateAutoImportMutation,
  useEditAutoImportMutation,
  useGetCategoriesQuery,
  useGetSocialProfilesQuery
} from 'models/graphql';
import { AppContext } from 'providers/AppProvider';
import { useContext } from 'react';
import { getApolloError } from '../../../../constants/apollo-errors';
import { ApolloErrorMapKeysType } from '../../../../models/apollo-errors';
import { showToast } from '../../../../helpers/showToast';
import { triggerErrorToast } from '../../../../helpers/triggerErrorToast';
import { useRequiredQueries } from '../../../../hooks/useRequiredQueries';
import { getApolloErrorMessage } from 'helpers/remapApolloErrors';

export type CreateEditImporterModalApolloProps = Omit<
  CreateEditImporterModalControllerProps,
  'createAutoImport' | 'editAutoImport' | 'socialProfiles' | 'categories' | 'loading'
> & {
  // ...
};

gql`
  mutation CreateAutoImport($workspaceId: ID!, $input: CreateAutoImportInput!) {
    createAutoImport(input: $input, workspaceId: $workspaceId) {
      ...AutoImportFields
      category {
        ...CategoryFields
      }
      socialProfiles {
        ...SocialProfileFields
        ... on PinterestSocialProfile {
          boards {
            ...CorePinterestBoardFields
          }
        }
      }
    }
  }
`;

gql`
  mutation EditAutoImport($autoImportId: ID!, $input: UpdateAutoImportInput!) {
    updateAutoImport(autoImportId: $autoImportId, input: $input) {
      ...AutoImportFields
      category {
        ...CategoryFields
      }
      socialProfiles {
        ...SocialProfileFields
        ... on PinterestSocialProfile {
          boards {
            ...CorePinterestBoardFields
          }
        }
      }
    }
  }
`;

export const CreateEditImporterModalApollo = (props: CreateEditImporterModalApolloProps): JSX.Element => {
  const { cache } = useApolloClient();
  const [createAutoImport] = useCreateAutoImportMutation();
  const [editAutoImport] = useEditAutoImportMutation();

  const { selectedWorkspaceId, setLimitReachedModalState } = useContext(AppContext);

  const socialProfilesQuery = useGetSocialProfilesQuery({
    variables: { workspaceId: selectedWorkspaceId }
  });

  const categoriesQuery = useGetCategoriesQuery({
    variables: { workspaceId: selectedWorkspaceId }
  });
  useRequiredQueries([socialProfilesQuery, categoriesQuery]);

  const createImportMutationHandler = async (
    options: CreateAutoImportMutationOptions,
    callback: (success: boolean) => void
  ) => {
    try {
      const { data, errors } = await createAutoImport(options);
      if (errors) {
        const detail = errors
          .map(error => getApolloError(error.message as ApolloErrorMapKeysType, 'createAutoImport'))
          .join('\n');
        showToast({
          summary: 'Error in creating the Auto Importer',
          detail,
          severity: 'error'
        });
        return;
      }
      const cacheData = cache.readQuery<GetAutoImportsQuery, GetAutoImportsQueryVariables>({
        query: GetAutoImportsDocument,
        variables: { workspaceId: selectedWorkspaceId }
      });
      if (data && cacheData && cacheData.workspace) {
        const updatedCache: GetAutoImportsQuery = {
          workspace: {
            ...cacheData.workspace,
            autoImports: [
              ...cacheData.workspace.autoImports,
              {
                ...data.createAutoImport,
                __typename: 'AutoImport'
              }
            ]
          }
        };

        cache.writeQuery({
          query: GetAutoImportsDocument,
          variables: { workspaceId: selectedWorkspaceId },
          data: updatedCache
        });
      }

      callback(true);
    } catch (e) {
      const error = e as unknown as ApolloError;
      if (getApolloErrorMessage(error) === 'PLAN_LIMIT_EXCEEDED') {
        setLimitReachedModalState({
          type: 'autoRSS',
          isOpen: true
        });
        callback(false);
        return;
      }
      triggerErrorToast('Error in creating the Auto Importer')(error);
      callback(false);
    }
  };

  const editImportMutationHandler = async (
    options: EditAutoImportMutationOptions,
    callback: (success: boolean) => void
  ) => {
    try {
      const { errors } = await editAutoImport(options);
      if (errors) {
        const detail = errors
          .map(error => getApolloError(error.message as ApolloErrorMapKeysType, 'updateAutoImport'))
          .join('\n');
        showToast({
          summary: 'Error in updating the Auto Importer',
          detail,
          severity: 'error'
        });
        return;
      }
      callback(true);
    } catch (e) {
      const error = e as unknown as ApolloError;
      triggerErrorToast('Error in updating the Auto Importer')(error);
      callback(false);
    }
  };

  return (
    <CreateEditImporterModalController
      {...props}
      loading={!socialProfilesQuery?.data || !categoriesQuery?.data}
      socialProfiles={
        // TODO we temporary disable tiktok - required by the specs: https://app.asana.com/0/1191520030670857/1207973088308036
        socialProfilesQuery?.data?.workspace.socialProfiles.filter(p => p.__typename !== 'TikTokSocialProfile') || []
      }
      categories={categoriesQuery?.data?.workspace.categories || []}
      createAutoImport={createImportMutationHandler}
      editAutoImport={editImportMutationHandler}
    />
  );
};
